import { useState } from "react";
import { StyleSheet, View } from "react-native";
import { Clock3 } from "lucide-react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomDropdown from "../../../../../components/common/general/CustomDropDown/CustomDropdown";
import { colors, radii, spacing, typographyScale } from "../../../../../styles/design-system";
import { CustomSnoozePopover } from "./CustomSnoozePopover";

type DropdownOption = {
  label: string;
  value: string;
};

type ScheduleAndSnoozePreferencesProps = {
  preferredSendTime: string;
  timezone: string;
  activeSnooze: "1 hour" | "1 week" | "Custom..." | null;
  snoozeAllUntil: string | null;
  animationsEnabled: boolean;
  onUserInteraction: () => void;
  onPreferredSendTimeChange: (nextValue: string) => void;
  onTimezoneChange: (nextValue: string) => void;
  onPresetSnoozeSelect: (option: "1 hour" | "1 week") => Promise<void>;
  onCustomSnoozeSelect: (snoozeUntilIso: string) => Promise<boolean>;
  onResumeSnooze: () => Promise<void>;
};

const NOTIFICATION_DROPDOWN_WIDTH = 130;
const SEND_TIME_OPTIONS: DropdownOption[] = [
  { label: "6:00 AM", value: "06:00" },
  { label: "7:00 AM", value: "07:00" },
  { label: "8:00 AM", value: "08:00" },
  { label: "9:00 AM", value: "09:00" },
  { label: "10:00 AM", value: "10:00" },
  { label: "11:00 AM", value: "11:00" },
  { label: "12:00 PM", value: "12:00" },
  { label: "1:00 PM", value: "13:00" },
  { label: "2:00 PM", value: "14:00" },
  { label: "3:00 PM", value: "15:00" },
  { label: "4:00 PM", value: "16:00" },
  { label: "5:00 PM", value: "17:00" },
  { label: "6:00 PM", value: "18:00" },
  { label: "7:00 PM", value: "19:00" },
  { label: "8:00 PM", value: "20:00" },
  { label: "9:00 PM", value: "21:00" },
  { label: "10:00 PM", value: "22:00" },
];
const TIMEZONE_OPTIONS: DropdownOption[] = [
  { label: "Eastern", value: "America/New_York" },
  { label: "Central", value: "America/Chicago" },
  { label: "Mountain", value: "America/Denver" },
  { label: "Pacific", value: "America/Los_Angeles" },
  { label: "Alaska", value: "America/Anchorage" },
  { label: "Hawaii", value: "Pacific/Honolulu" },
  { label: "UTC", value: "UTC" },
];

export const ScheduleAndSnoozePreferences = ({
  preferredSendTime,
  timezone,
  activeSnooze,
  snoozeAllUntil,
  animationsEnabled,
  onUserInteraction,
  onPreferredSendTimeChange,
  onTimezoneChange,
  onPresetSnoozeSelect,
  onCustomSnoozeSelect,
  onResumeSnooze,
}: ScheduleAndSnoozePreferencesProps) => {
  const [openDropdownKey, setOpenDropdownKey] = useState<"preferredSendTime" | "timezone" | null>(null);
  const isPreferredSendTimeOpen = openDropdownKey === "preferredSendTime";
  const isTimezoneOpen = openDropdownKey === "timezone";
  const isSectionOpen = isPreferredSendTimeOpen || isTimezoneOpen;
  const resolvedTimezoneValue = TIMEZONE_OPTIONS.some((option) => option.value === timezone)
    ? timezone
    : "America/New_York";

  const handleDropdownOpenChange = (dropdownKey: "preferredSendTime" | "timezone", isOpen: boolean) => {
    setOpenDropdownKey((previous) => {
      if (isOpen) {
        return dropdownKey;
      }

      return previous === dropdownKey ? null : previous;
    });
  };

  return (
    <View style={[styles.sectionContainer, isSectionOpen && styles.sectionContainerOpen]}>
      <View style={[styles.sectionHeader, styles.sectionHeaderWithBottomSpacing]}>
        <Clock3 size={18} color={colors.grey[500]} />
        <CustomText text="Schedule & snooze" useTranslationText={false} style={styles.sectionHeaderTitle} />
      </View>

      <View style={[styles.selectFieldRow, isPreferredSendTimeOpen && styles.selectFieldRowOpen]}>
        <CustomText text="Preferred send time" useTranslationText={false} style={styles.selectLabel} />
        <CustomDropdown
          options={SEND_TIME_OPTIONS}
          value={preferredSendTime}
          onChange={(nextValue) => {
            setOpenDropdownKey(null);
            onPreferredSendTimeChange(nextValue);
          }}
          accessibilityLabel="Preferred send time"
          open={isPreferredSendTimeOpen}
          onOpenChange={(isOpen) => {
            handleDropdownOpenChange("preferredSendTime", isOpen);
          }}
          containerStyle={[styles.selectDropdown, isPreferredSendTimeOpen && styles.selectDropdownOpen]}
          dropdownStyle={styles.selectDropdownField}
          dropdownContainerStyle={styles.selectDropdownMenu}
          hideItemBorders
        />
      </View>

      <View style={[styles.selectFieldRow, isTimezoneOpen && styles.selectFieldRowOpen]}>
        <CustomText text="Timezone" useTranslationText={false} style={styles.selectLabel} />
        <CustomDropdown
          options={TIMEZONE_OPTIONS}
          value={resolvedTimezoneValue}
          onChange={(nextValue) => {
            setOpenDropdownKey(null);
            onTimezoneChange(nextValue);
          }}
          accessibilityLabel="Notification timezone"
          open={isTimezoneOpen}
          onOpenChange={(isOpen) => {
            handleDropdownOpenChange("timezone", isOpen);
          }}
          containerStyle={[styles.selectDropdown, isTimezoneOpen && styles.selectDropdownOpen]}
          dropdownStyle={styles.selectDropdownField}
          dropdownContainerStyle={styles.selectDropdownMenu}
          hideItemBorders
        />
      </View>

      <View style={styles.sectionDivider} />

      <View style={styles.snoozeContent}>
        <CustomText text="Snooze all notifications" useTranslationText={false} style={styles.preferenceTitle} />
        <CustomText
          text="Temporarily pause all notifications"
          useTranslationText={false}
          style={styles.preferenceHint}
        />
        <CustomSnoozePopover
          activeSnooze={activeSnooze}
          snoozeAllUntil={snoozeAllUntil}
          animationsEnabled={animationsEnabled}
          onUserInteraction={onUserInteraction}
          onPresetSnoozeSelect={onPresetSnoozeSelect}
          onCustomSnoozeSelect={onCustomSnoozeSelect}
          onResumeSnooze={onResumeSnooze}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sectionContainer: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    padding: spacing.lg,
    position: "relative",
    overflow: "visible",
  },
  sectionContainerOpen: {
    zIndex: 5000,
    elevation: 5000,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  sectionHeaderWithBottomSpacing: {
    marginBottom: spacing.md,
  },
  sectionHeaderTitle: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    fontWeight: "600",
    color: colors.grey[800],
  },
  selectFieldRow: {
    marginTop: spacing.sm,
    maxWidth: 320,
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    position: "relative",
    overflow: "visible",
  },
  selectFieldRowOpen: {
    zIndex: 6000,
    elevation: 6000,
  },
  selectLabel: {
    color: colors.grey[600],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    width: 88,
  },
  selectDropdown: {
    flex: 1,
    position: "relative",
    overflow: "visible",
  },
  selectDropdownOpen: {
    zIndex: 7000,
    elevation: 7000,
  },
  selectDropdownField: {
    height: 30,
    width: NOTIFICATION_DROPDOWN_WIDTH,
    borderColor: colors.grey[300],
    backgroundColor: colors.white,
    borderRadius: radii.md,
    shadowColor: "transparent",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  selectDropdownMenu: {
    width: NOTIFICATION_DROPDOWN_WIDTH,
    borderColor: colors.grey[300],
    backgroundColor: colors.white,
    shadowColor: "transparent",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: colors.grey[200],
    marginTop: spacing.md,
    marginBottom: spacing.md,
  },
  snoozeContent: {
    gap: spacing.xxs,
  },
  preferenceTitle: {
    color: colors.grey[800],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  preferenceHint: {
    color: colors.grey[500],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
});
